/*
  Now You Live wishlist.

  Native to the theme, no app. The list lives in localStorage as product
  handles, and a share link carries those handles in the URL so a wishlist can
  be sent to anyone.

  Security notes, because this reads from a URL:
    - Handles from the URL are untrusted. They are validated against a strict
      pattern and capped in count before being used for anything.
    - Nothing from the URL is ever written into the DOM as markup. Handles are
      only ever used to build a same origin fetch path.
    - Card markup comes from our own Section Rendering API, so the one card
      component stays the single source of truth rather than being duplicated
      in JavaScript.
    - No customer data is stored or transmitted. Handles are public catalogue
      data already visible in any product URL.
*/

(() => {
  const STORAGE_KEY = 'nyl:wishlist:v1';
  const MAX_ITEMS = 100;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]{0,119}$/;
  const VARIANT_PATTERN = /^[0-9]{1,20}$/;

  /**
   * One saved item is a product and, where the visitor had already chosen
   * one, the variant they chose. Saving the variant is what lets the wishlist
   * page put the whole list in the bag without asking for sizes a second time.
   *
   * Entries used to be bare handle strings, and lists saved then are still in
   * storage, so a string is read as a product with no size yet.
   */
  const toEntry = (value) => {
    if (typeof value === 'string') {
      return HANDLE_PATTERN.test(value) ? { handle: value, variant: null } : null;
    }
    if (!value || typeof value !== 'object') return null;

    const handle = value.handle;
    if (typeof handle !== 'string' || !HANDLE_PATTERN.test(handle)) return null;

    const variant = value.variant == null ? '' : String(value.variant);
    return { handle, variant: VARIANT_PATTERN.test(variant) ? variant : null };
  };

  /** Reads the stored list. Always returns an array, even if storage is unavailable. */
  const read = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      const seen = new Set();
      const entries = [];
      parsed.forEach((value) => {
        const entry = toEntry(value);
        if (!entry || seen.has(entry.handle)) return;
        seen.add(entry.handle);
        entries.push(entry);
      });
      return entries.slice(0, MAX_ITEMS);
    } catch (error) {
      // Private mode, blocked storage, or corrupt JSON. Behave as empty.
      return [];
    }
  };

  const write = (entries) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ITEMS)));
    } catch (error) {
      // Storage full or blocked. The in-memory UI still reflects the change.
    }
  };

  function isValidHandle(value) {
    return typeof value === 'string' && HANDLE_PATTERN.test(value);
  }

  const handlesOf = (entries) => entries.map((entry) => entry.handle);

  const announce = (entries) => {
    document.dispatchEvent(
      new CustomEvent('nyl:wishlist:change', {
        detail: { entries, handles: handlesOf(entries) },
      })
    );
  };

  const api = {
    /** Every saved item, as {handle, variant}. */
    all: read,

    /** Just the product handles, for anything that only cares what is saved. */
    handles() {
      return handlesOf(read());
    },

    has(handle) {
      return isValidHandle(handle) && read().some((entry) => entry.handle === handle);
    },

    /** The size saved against a product, or null if none was chosen. */
    variantFor(handle) {
      return read().find((entry) => entry.handle === handle)?.variant ?? null;
    },

    /**
     * Saves a product, carrying the chosen variant when the surface knows one.
     * Saving again with a variant fills in a size that was saved without one,
     * which is how a heart pressed on a card gains a size later.
     */
    add(handle, variant) {
      const entry = toEntry({ handle, variant });
      if (!entry) return read();

      const entries = read();
      const existing = entries.find((item) => item.handle === entry.handle);
      if (existing) {
        if (entry.variant) existing.variant = entry.variant;
      } else {
        entries.unshift(entry);
      }

      const capped = entries.slice(0, MAX_ITEMS);
      write(capped);
      announce(capped);
      return capped;
    },

    toggle(handle, variant) {
      if (!isValidHandle(handle)) return read();
      return this.has(handle) ? this.remove(handle) : this.add(handle, variant);
    },

    remove(handle) {
      if (!isValidHandle(handle)) return read();
      const entries = read().filter((entry) => entry.handle !== handle);
      write(entries);
      announce(entries);
      return entries;
    },

    /** Merges a list in, keeping what is already saved and the cap. */
    addMany(incoming) {
      const clean = (incoming || []).map(toEntry).filter(Boolean);
      if (clean.length === 0) return read();

      const entries = read();
      clean.forEach((entry) => {
        const existing = entries.find((item) => item.handle === entry.handle);
        if (existing) {
          if (entry.variant) existing.variant = entry.variant;
        } else {
          entries.unshift(entry);
        }
      });

      const capped = entries.slice(0, MAX_ITEMS);
      write(capped);
      announce(capped);
      return capped;
    },

    /**
     * Swaps one product for another in its place, for a colourway change made
     * on the wishlist page. Position is kept so the list does not reshuffle
     * under the visitor, and the size does not carry over, because a size run
     * belongs to the colourway it was chosen on.
     */
    replace(from, to) {
      if (!isValidHandle(from) || !isValidHandle(to)) return read();
      const entries = read();
      const index = entries.findIndex((entry) => entry.handle === from);
      if (index === -1) return entries;

      if (entries.some((entry) => entry.handle === to)) entries.splice(index, 1);
      else entries[index] = { handle: to, variant: null };

      write(entries);
      announce(entries);
      return entries;
    },

    /** Removes a set in one write, so a bulk add costs one re-render. */
    removeMany(handles) {
      const drop = new Set((handles || []).filter(isValidHandle));
      if (drop.size === 0) return read();

      const entries = read();
      const kept = entries.filter((entry) => !drop.has(entry.handle));
      if (kept.length === entries.length) return entries;

      write(kept);
      announce(kept);
      return kept;
    },

    clear() {
      write([]);
      announce([]);
      return [];
    },

    /**
     * A shared list, as handle:variant pairs. Untrusted, so every part is
     * validated and the whole thing capped before any of it is used.
     */
    fromShareParam(search = window.location.search) {
      const raw = new URLSearchParams(search).get('items');
      if (!raw) return [];

      const seen = new Set();
      const entries = [];
      raw.split(',').forEach((part) => {
        const [handle, variant] = part.trim().toLowerCase().split(':');
        const entry = toEntry({ handle, variant });
        if (!entry || seen.has(entry.handle)) return;
        seen.add(entry.handle);
        entries.push(entry);
      });
      return entries.slice(0, MAX_ITEMS);
    },

    shareUrl(pageUrl) {
      const entries = read();
      if (entries.length === 0) return null;

      const url = new URL(pageUrl || window.location.href, window.location.origin);
      url.search = '';
      url.searchParams.set(
        'items',
        entries.map((entry) => (entry.variant ? `${entry.handle}:${entry.variant}` : entry.handle)).join(',')
      );
      return url.toString();
    },
  };

  window.NylWishlist = api;

  /* Card and header buttons --------------------------------------------- */

  const syncToggles = (handles) => {
    document.querySelectorAll('[data-nyl-wishlist-toggle]').forEach((button) => {
      const handle = button.dataset.productHandle;
      button.setAttribute('aria-pressed', handles.includes(handle) ? 'true' : 'false');
    });
  };

  const syncCount = (handles) => {
    document.querySelectorAll('[data-nyl-wishlist-count]').forEach((node) => {
      node.textContent = String(handles.length);
      node.hidden = handles.length === 0;
      // The heart it sits on fills while the list has anything in it.
      node.closest('a')?.toggleAttribute('data-nyl-wishlist-has', handles.length > 0);
    });
  };

  const syncAll = (entries) => {
    const handles = handlesOf(entries);
    syncToggles(handles);
    syncCount(handles);
  };

  /* Toast ----------------------------------------------------------------
     Placed from the pressed heart's own box rather than parented to it, so no
     ancestor's overflow can clip it, and it works the same from a card, the
     quick add panel or anywhere a heart is added later.
  */
  let toastTimer;
  let toastAnchor = null;

  const hideToast = () => {
    const el = document.querySelector('[data-nyl-toast]');
    toastAnchor = null;
    if (!el) return;
    clearTimeout(toastTimer);
    el.classList.remove('is-visible');
    el.hidden = true;
  };

  /** Keeps it under its heart while the page moves, rather than vanishing. */
  const placeToast = () => {
    const el = document.querySelector('[data-nyl-toast]');
    if (!el || !toastAnchor) return;

    const box = toastAnchor.getBoundingClientRect();
    // Gone from the screen entirely, so the toast goes with it.
    if (box.bottom < 0 || box.top > window.innerHeight) {
      hideToast();
      return;
    }

    const gap = 8;
    const left = Math.min(
      Math.max(gap, box.left + box.width / 2 - el.offsetWidth / 2),
      window.innerWidth - el.offsetWidth - gap
    );
    // Under the heart, unless that would fall off the foot of the screen.
    const below = box.bottom + gap;
    const top = below + el.offsetHeight > window.innerHeight - gap
      ? Math.max(gap, box.top - el.offsetHeight - gap)
      : below;

    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
  };

  const TOAST_MS = 2500;

  // A modal dialog, the quick add panel, sits in the browser's top layer,
  // above everything else on the page, so a toast left in the page renders
  // underneath it. It moves into the open dialog its heart belongs to, and
  // home again for a heart on the page. The dialog has no transform, so the
  // fixed position still reads against the screen.
  let toastHome = null;
  const layerToast = (el, anchor) => {
    if (!toastHome) toastHome = el.parentNode;
    const target = anchor.closest('dialog[open]') || toastHome;
    if (target && el.parentNode !== target) target.appendChild(el);
  };

  const showToast = (anchor, message) => {
    const el = document.querySelector('[data-nyl-toast]');
    if (!el || !anchor) return;
    layerToast(el, anchor);

    // The whole pill is the link. Keep it on whatever the header points at,
    // so one setting drives both.
    const href = document.querySelector('[data-nyl-wishlist-link]')?.getAttribute('href');
    if (href) el.setAttribute('href', href);

    const text = el.querySelector('[data-nyl-toast-text]');
    // Cleared first so the live region announces a repeat of the same message.
    if (text) {
      text.textContent = '';
      text.textContent = message;
    }

    el.hidden = false;
    toastAnchor = anchor;
    placeToast();
    el.classList.add('is-visible');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, TOAST_MS);
  };

  // For a surface that saves without a heart of its own, such as the cart's
  // Save For Later: the message shows under whatever it anchors to.
  api.notify = (anchor, message) => {
    if (anchor && message) showToast(anchor, message);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-nyl-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();

    const handle = button.dataset.productHandle;
    // A surface that already knows the size, the quick add panel or a product
    // page, passes it along so the list can reach the bag without asking again.
    const variant = button.dataset.variantId || null;
    const added = !api.has(handle);
    api.toggle(handle, variant);

    const strings = document.querySelector('[data-nyl-toast]')?.dataset ?? {};
    showToast(button, added ? strings.added : strings.removed);
  });

  // Long enough to reach the link, and it waits while the pointer is on it.
  document.addEventListener('pointerenter', (event) => {
    if (event.target.closest?.('[data-nyl-toast]')) clearTimeout(toastTimer);
  }, true);

  document.addEventListener('pointerleave', (event) => {
    if (event.target.closest?.('[data-nyl-toast]')) {
      toastTimer = setTimeout(hideToast, 700);
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    if (event.target.closest('[data-nyl-toast]')) clearTimeout(toastTimer);
  });

  // It is placed against a box that scrolling moves, so it follows rather
  // than disappearing the moment the page shifts under it.
  window.addEventListener('scroll', placeToast, { passive: true });
  window.addEventListener('resize', placeToast, { passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hideToast();
  });

  document.addEventListener('nyl:wishlist:change', (event) => {
    syncAll(event.detail.entries);
  });

  // Anything that injects markup, a colourway swap or the quick add panel,
  // brings hearts that have never been told what is on the list.
  document.addEventListener('nyl:content', () => syncAll(read()));

  /**
   * The wishlist is what has not been bought yet, so anything that reaches
   * the bag leaves it. The cart's Save For Later is the way back.
   */
  document.addEventListener('nyl:cart:added', (event) => {
    const handles = event.detail?.handles;
    if (Array.isArray(handles) && handles.length > 0) api.removeMany(handles);
  });

  // Another tab changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) syncAll(read());
  });

  const init = () => syncAll(read());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Sections re-render in the theme editor, so re-sync when they do.
  document.addEventListener('shopify:section:load', init);
})();
